// Figma "ESG - Export Dropdown Open" (node 18249:158587): the Export button in
// EsgComponent.js's page header opens a small dropdown with "Export as PDF" and "Export as
// XLSX". Both link straight to fixed, pre-generated report files — hardcoded per
// instruction, not built from the on-screen ESG data at all anymore. Previously "Export as
// PDF" rendered a PDF client-side (@react-pdf/renderer + EsgReportPdf.js/pdfData.js) and
// "Export as CSV" built a CSV client-side from the live `data` (also pdfData.js) — both are
// dropped now that export targets these two static files instead, so this no longer needs
// `data`/`dashboardAccount` as props at all.
//
// TEMPORARY: these two files are served from public/esg-reports/ (bundled straight into
// this app's own static assets) rather than the real S3 location
// (s3://stake-sftp-files/feed_report/...), because that bucket returns AccessDenied on a
// direct URL — it isn't public, and generating a real presigned URL needs a backend route
// that doesn't exist yet. Swap PDF_REPORT_URL/XLSX_REPORT_URL for that backend-provided URL
// once it's built; until then these local copies need to be manually refreshed if the
// source reports change.
import React, {useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, ButtonBase, ClickAwayListener, Popper, Typography} from '@mui/material';

import {tokens} from '../../../themes/ThemeCustomization';

import exportIcon from '../../../assets/esg/export.svg';
import exportPdfIcon from '../../../assets/esg/export-pdf-icon.svg';
import exportCsvIcon from '../../../assets/esg/export-csv-icon.svg';

const PDF_REPORT_URL = '/esg-reports/Progress_Stake_Q2_2026_ESG_Resident_Impact_Report.pdf';
const XLSX_REPORT_URL = '/esg-reports/Progress_Stake_Q2_2026_Impact_Report_Detailed_Data.xlsx';

const MenuRow = ({icon, label, href, download, onClick}) => (
    <ButtonBase
        component="a"
        href={href}
        download={download}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        sx={{
            width: '100%',
            height: '36px',
            borderRadius: '6px',
            justifyContent: 'flex-start',
            gap: 1,
            px: 1,
            '&:hover': {backgroundColor: tokens.color.neutral100}
        }}
    >
        <Box component="img" src={icon} alt="" sx={{width: 16, height: 16}} />
        <Typography variant="body2">{label}</Typography>
    </ButtonBase>
);

MenuRow.propTypes = {
    icon: PropTypes.string,
    label: PropTypes.node,
    href: PropTypes.string,
    download: PropTypes.bool,
    onClick: PropTypes.func
};

const ExportMenu = () => {
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);

    return (
        <Box ref={anchorRef}>
            <ButtonBase
                onClick={() => setOpen(true)}
                sx={{
                    height: '36px',
                    px: 1.5,
                    columnGap: 1,
                    borderRadius: '6px',
                    border: `1px solid ${tokens.color.neutral200}`,
                    backgroundColor: tokens.color.white
                }}
            >
                <Box component="img" src={exportIcon} alt="" sx={{width: 16, height: 16}} />
                <Typography variant="body2" fontWeight={500}>
                    Export
                </Typography>
            </ButtonBase>

            <Popper open={open} anchorEl={anchorRef.current} placement="bottom-end" sx={{zIndex: 1300}}>
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <Box
                        sx={{
                            mt: 0.5,
                            width: '190px',
                            p: 1,
                            backgroundColor: tokens.color.white,
                            border: `1px solid ${tokens.color.neutral200}`,
                            borderRadius: '12px',
                            boxShadow: tokens.shadow.toast
                        }}
                    >
                        <MenuRow icon={exportPdfIcon} label="Export as PDF" href={PDF_REPORT_URL} download onClick={() => setOpen(false)} />
                        <MenuRow icon={exportCsvIcon} label="Export as XLSX" href={XLSX_REPORT_URL} download onClick={() => setOpen(false)} />
                    </Box>
                </ClickAwayListener>
            </Popper>
        </Box>
    );
};

export default ExportMenu;
